import { sharedLib } from "@shared/services/shared.service";
import { useSelector } from "react-redux";
import { marketplaceSelectors } from "@shared/slices/marketplace.slice";
import { useEffect, useMemo, useState } from "react";
import { GroupsSort } from "@shared/features/newMarketplace/types";
import { MarketplaceSubGroup, SortingOrder } from "@shared/types";
import { getSortedDataBySortSelect } from "@shared/utils/general";
import {
  getPnlSortCallback,
  getRiskSortCallback,
} from "@shared/features/newMarketplace/utils";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import {
  MARKETPLACE_SORT_TO_PATH,
  MARKETPLACE_STRING_SORTS,
  MIN_NUM_OF_REVIEWS,
  OPPOSITE_SORTS,
} from "@shared/features/newMarketplace/constants";
import { skipToken } from "@reduxjs/toolkit/query";
import { useUser } from "@shared/utils/hooks/misc";

interface UseMarketplaceProps {
  searchValue: string;
  showFullList?: boolean;
}

const useMarketplaceGroups = ({
  searchValue,
  showFullList,
}: UseMarketplaceProps) => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { user } = useUser();
  const showFullListToggle = !!user?.permission_level;

  const [isFetched, setIsFetched] = useState(false);
  const [sortBy, setSortBy] = useState<GroupsSort>(GroupsSort.SCORE);
  const [sortDirection, setSortDirection] = useState<SortingOrder>(
    SortingOrder.DESCEND
  );
  const marketplaceQueryStatus =
    sharedLib.sharedApi.marketplace.useGetMarketplaceInfoQuery();
  const fullMarketplaceQueryStatus =
    sharedLib.sharedApi.marketplace.useGetMarketplaceInfoFullQuery(
      showFullList || isFetched ? undefined : skipToken
    );

  const getMarketplaceInfoQuery = showFullList
    ? fullMarketplaceQueryStatus
    : marketplaceQueryStatus;

  useEffect(() => {
    if (showFullList && !isFetched && fullMarketplaceQueryStatus.isSuccess) {
      setIsFetched(true);
    }
  }, [showFullList, isFetched, fullMarketplaceQueryStatus.isSuccess]);

  const groupsSelector = useMemo(
    () => marketplaceSelectors.selectFilteredSubGroupsData(showFullList),
    [showFullList]
  );
  const filteredMarketplaceGroups = useSelector(groupsSelector);

  const filteredGroups = useMemo(() => {
    return filteredMarketplaceGroups
      .filter((subGroup) =>
        subGroup.name.toLowerCase().includes(searchValue.toLowerCase())
      )
      .map((subGroup) => {
        if (subGroup.num_of_reviewers < MIN_NUM_OF_REVIEWS)
          return { ...subGroup, avg_stars: -1 };
        return subGroup;
      });
  }, [searchValue, filteredMarketplaceGroups]);

  const groupsSortToPath = useMemo(
    () => ({
      ...MARKETPLACE_SORT_TO_PATH,
      [GroupsSort.RISK]: getRiskSortCallback(sortDirection),
      [GroupsSort.SIX_MONTH_PNL]: getPnlSortCallback(
        180,
        displayCurrency,
        sortDirection
      ),
      [GroupsSort.THREE_MONTH_PNL]: getPnlSortCallback(
        90,
        displayCurrency,
        sortDirection
      ),
      [GroupsSort.ONE_MONTH_PNL]: getPnlSortCallback(
        30,
        displayCurrency,
        sortDirection
      ),
    }),
    [displayCurrency, sortDirection]
  );

  const sortedGroups = useMemo(() => {
    return getSortedDataBySortSelect<MarketplaceSubGroup, GroupsSort>(
      filteredGroups,
      sortBy,
      sortDirection,
      groupsSortToPath,
      MARKETPLACE_STRING_SORTS
    );
  }, [sortBy, sortDirection, filteredGroups, groupsSortToPath]);

  const onSetSortBy = (sortBy: GroupsSort) => {
    setSortBy(sortBy);
    if (OPPOSITE_SORTS.includes(sortBy)) {
      setSortDirection(SortingOrder.DESCEND);
    } else {
      setSortDirection(SortingOrder.ASCEND);
    }
  };

  return {
    getMarketplaceInfoQuery,
    sortedGroups,
    sortBy,
    setSortBy: onSetSortBy,
    sortDirection,
    setSortDirection,
    showFullListToggle,
  };
};

export default useMarketplaceGroups;
